// src/hooks/useOffline.ts
// Central offline state management — React context + hook.
// Provides connectivity status, sync queue counts, and sync controls to the entire app.

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../services/offlineDb';
import syncEngine, { onSyncEvent, SyncEvent } from '../services/syncEngine';
import { API_BASE_URL } from '../constants';
import { useQueryClient } from '@tanstack/react-query';

// ── Context Shape ──

export interface OfflineContextValue {
  isOnline: boolean;           // real-time connectivity
  pendingCount: number;        // items in sync queue
  failedCount: number;         // items that failed to sync
  isSyncing: boolean;          // sync engine active
  lastSyncTime: Date | null;   // last successful full sync
  syncNow: () => Promise<void>;// manual trigger
  retryFailed: () => Promise<void>; // retry failed ops
  clearFailed: () => void;     // dismiss failed items
  lastSyncEvent: SyncEvent | null; // most recent sync event (for toasts)
}

const OfflineContext = createContext<OfflineContextValue>({
  isOnline: true,
  pendingCount: 0,
  failedCount: 0,
  isSyncing: false,
  lastSyncTime: null,
  syncNow: async () => {},
  retryFailed: async () => {},
  clearFailed: () => {},
  lastSyncEvent: null,
});

export const useOffline = () => useContext(OfflineContext);

// ── Health-check ping (guards against captive portals) ──

const PING_INTERVAL_MS = 15_000; // 15 seconds (faster detection for restaurant environments)

async function checkRealConnectivity(): Promise<boolean> {
  try {
    const healthUrl = API_BASE_URL.replace(/\/api\/v\d+$/, '') + '/api/v1/health';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(healthUrl, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeout);
    // Any HTTP response (even 502/503) means the network is working.
    // Only treat fetch-level errors (no network, DNS failure, timeout) as offline.
    // A 502 from a reverse proxy means internet is fine — the backend is just down.
    return true;
  } catch (err) {
    console.warn('[useOffline] checkRealConnectivity failed:', err);
    return false;
  }
}

// ── Provider Component ──

export const OfflineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [lastSyncEvent, setLastSyncEvent] = useState<SyncEvent | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const queryClient = useQueryClient();

  // ── Refresh queue counts ──
  const refreshCounts = useCallback(async () => {
    try {
      const [pending, failed] = await Promise.all([
        db.pendingCount(),
        db.failedCount(),
      ]);
      setPendingCount(pending);
      setFailedCount(failed);
    } catch {
      // IndexedDB may not be available in some contexts
    }
  }, []);

  // ── Connectivity Detection ──
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Verify with a real ping
      checkRealConnectivity().then((real) => {
        if (real) {
          setIsOnline(true);
        }
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic ping when navigator says online (captive portal guard)
    pingIntervalRef.current = setInterval(() => {
      if (navigator.onLine) {
        checkRealConnectivity().then((real) => {
          setIsOnline(real);
        });
      }
    }, PING_INTERVAL_MS);

    // Also do an immediate ping on mount to establish initial state
    checkRealConnectivity().then((real) => {
      setIsOnline(real);
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    };
  }, []);

  // ── Sync Engine Events ──
  useEffect(() => {
    const unsubs = [
      onSyncEvent('sync:started', (e) => {
        setIsSyncing(true);
        setLastSyncEvent(e);
      }),
      onSyncEvent('sync:completed', (e) => {
        setLastSyncEvent(e);
        refreshCounts();
      }),
      onSyncEvent('sync:failed', (e) => {
        setLastSyncEvent(e);
        refreshCounts();
      }),
      onSyncEvent('sync:conflict', (e) => {
        setLastSyncEvent(e);
        refreshCounts();
      }),
      onSyncEvent('sync:allCompleted', (e) => {
        setIsSyncing(false);
        setLastSyncTime(new Date());
        setLastSyncEvent(e);
        refreshCounts();
        queryClient.invalidateQueries();
      }),
      onSyncEvent('sync:progress', (e) => {
        setLastSyncEvent(e);
      }),
      onSyncEvent('sync:queueChanged', () => {
        refreshCounts();
      }),
    ];

    return () => unsubs.forEach((fn) => fn());
  }, [refreshCounts]);

  // ── Register auto-sync and refresh counts on mount ──
  useEffect(() => {
    syncEngine.registerAutoSync();
    refreshCounts();
  }, [refreshCounts]);

  // ── Trigger sync when isOnline transitions false → true ──
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    if (isOnline && !prevOnlineRef.current) {
      // Connectivity just restored — trigger sync
      console.log('[OfflineProvider] Connectivity restored (false → true) — starting sync');
      syncEngine.startSync();
    }
    prevOnlineRef.current = isOnline;
  }, [isOnline]);

  // ── Public Actions ──
  const syncNow = useCallback(async () => {
    if (!isOnline) return;
    await syncEngine.startSync();
  }, [isOnline]);

  const retryFailed = useCallback(async () => {
    await syncEngine.retryFailed();
  }, []);

  const clearFailedFn = useCallback(() => {
    syncEngine.clearFailed();
  }, []);

  const value: OfflineContextValue = {
    isOnline,
    pendingCount,
    failedCount,
    isSyncing,
    lastSyncTime,
    syncNow,
    retryFailed,
    clearFailed: clearFailedFn,
    lastSyncEvent,
  };

  return React.createElement(OfflineContext.Provider, { value }, children);
};

export default useOffline;
